import React, { useEffect, useState, useMemo, useContext } from 'react'
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  HeaderLeft,
  HeaderTitleSection,
  ModalTitle,
  HeaderSubtitle,
  HeaderRight,
  HeaderMetricsGroup,
  HeaderMetricBadge,
  MetricLabel,
  MetricValue,
  CloseButton,
  ModalBody,
  TableContainer,
  GradeTable,
  TableHead,
  TableBody,
  TableFooter,
  CellTam,
  CellGrade,
  QuantityInput,
  CellRemainingOk,
  CellRemainingPending,
  CellRemainingExceeded,
  StatusNotice,
  ModalFooter,
  CancelButton,
  SaveButton,
} from './style'
import { X, Save, CheckCircle2, AlertCircle } from 'lucide-react'
import axios from '../../../../config/axios'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import { OrdenoLoading } from '../../../OrdenoLoading'

type StageItem = {
  id: number
  serviceStageId?: number
  stageId?: number
  stepId?: number
  name: string
  sectorName?: string
  completed: boolean
  index: number
}

type GradeItem = {
  size: string
  plannedQuantity: number
  orderItemGradeId?: number
}

type DistributionItem = {
  id?: number
  size: string
  quantity: number
  stageId?: number
  serviceStageId?: number
  stepId?: number
  orderItemGradeId?: number
}

type ServiceInfo = {
  id: number
  codeOfOP: string
  productName: string
  productCode: string
  collectionName: string
  colorName: string
  clientName: string
  deliveryDate: string | null
  ticketId?: number
  currentStepId?: number
}

type ManageGradeModalProps = {
  isOpen: boolean
  serviceId: number | null
  ticketId?: number | null
  onClose: () => void
  onSuccess?: () => void
}

const DEFAULT_FALLBACK_STAGES: StageItem[] = [
  { id: 1, name: 'Corte', completed: false, index: 0 },
  { id: 2, name: 'Costura', completed: false, index: 1 },
  { id: 3, name: 'Acabamento', completed: false, index: 2 },
  { id: 4, name: 'Revisão', completed: false, index: 3 },
  { id: 5, name: 'Concluído', completed: false, index: 4 },
]

const DEFAULT_FALLBACK_GRADES: GradeItem[] = [
  { size: '38', plannedQuantity: 1 },
  { size: '40', plannedQuantity: 3 },
  { size: '42', plannedQuantity: 3 },
  { size: '44', plannedQuantity: 2 },
  { size: '46', plannedQuantity: 1 },
]

const ManageGradeModal: React.FC<ManageGradeModalProps> = ({
  isOpen,
  serviceId,
  ticketId,
  onClose,
  onSuccess,
}) => {
  const { setFeedback } = useContext(FeedbackContext)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [service, setService] = useState<ServiceInfo | null>(null)
  const [stages, setStages] = useState<StageItem[]>([])
  const [grades, setGrades] = useState<GradeItem[]>([])
  const [allocationMatrix, setAllocationMatrix] = useState<Record<string, Record<number, number>>>({})

  const loadDistribution = async () => {
    if (!serviceId) return
    try {
      setLoading(true)
      const res = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/service-stage-grade/${serviceId}`)
      const data = res.data?.data || res.data
      if (!data) return

      setService(data.service)
      const rawStages: StageItem[] = (data.stages && data.stages.length > 0) ? data.stages : DEFAULT_FALLBACK_STAGES
      const uncompletedStages = rawStages.filter((s) => !s.completed)
      const fetchedStages = uncompletedStages.length > 0 ? uncompletedStages : rawStages

      const fetchedGrades: GradeItem[] = (data.grades && data.grades.length > 0) ? data.grades : DEFAULT_FALLBACK_GRADES
      const fetchedDistributions: DistributionItem[] = data.distributions || []

      setStages(fetchedStages)
      setGrades(fetchedGrades)

      const matrix: Record<string, Record<number, number>> = {}
      fetchedGrades.forEach((g) => {
        matrix[g.size] = {}
        fetchedStages.forEach((s) => {
          matrix[g.size][s.id] = 0
        })
      })

      fetchedDistributions.forEach((dist) => {
        const matchingStage = fetchedStages.find(
          (s) =>
            (dist.serviceStageId && s.serviceStageId === dist.serviceStageId) ||
            (dist.stageId && s.stageId === dist.stageId) ||
            (dist.stepId && s.stepId === dist.stepId) ||
            s.id === dist.stageId ||
            s.id === dist.serviceStageId,
        )
        const targetStageId = matchingStage ? matchingStage.id : dist.stageId || dist.serviceStageId || dist.stepId
        if (targetStageId) {
          if (!matrix[dist.size]) {
            matrix[dist.size] = {}
          }
          matrix[dist.size][targetStageId] = (matrix[dist.size][targetStageId] || 0) + dist.quantity
        }
      })

      const hasAnyDistribution = Object.values(matrix).some((stageMap) =>
        Object.values(stageMap).some((qty) => qty > 0),
      )

      if (!hasAnyDistribution && fetchedStages.length > 0) {
        const firstStageId = fetchedStages[0].id
        fetchedGrades.forEach((g) => {
          if (matrix[g.size]) {
            matrix[g.size][firstStageId] = g.plannedQuantity
          }
        })
      }

      setAllocationMatrix(matrix)
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message || 'Erro ao carregar distribuição de grade.',
        statusOk: false,
      })
      setStages(DEFAULT_FALLBACK_STAGES)
      setGrades(DEFAULT_FALLBACK_GRADES)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen && serviceId) {
      loadDistribution()
    }
  }, [isOpen, serviceId])

  const handleQuantityChange = (size: string, stageId: number, value: number) => {
    const sanitized = Math.max(0, isNaN(value) ? 0 : Math.floor(value))
    setAllocationMatrix((prev) => ({
      ...prev,
      [size]: {
        ...(prev[size] || {}),
        [stageId]: sanitized,
      },
    }))
  }

  const stageTotals = useMemo(() => {
    const totals: Record<number, number> = {}
    stages.forEach((s) => {
      totals[s.id] = 0
    })
    Object.values(allocationMatrix).forEach((stageMap) => {
      Object.entries(stageMap).forEach(([stIdStr, qty]) => {
        const stId = Number(stIdStr)
        totals[stId] = (totals[stId] || 0) + (Number(qty) || 0)
      })
    })
    return totals
  }, [stages, allocationMatrix])

  const sizeSummaries = useMemo(() => {
    return grades.map((grade) => {
      const stageMap = allocationMatrix[grade.size] || {}
      const distributed = Object.values(stageMap).reduce((acc, curr) => acc + (Number(curr) || 0), 0)
      const remaining = grade.plannedQuantity - distributed
      let status: 'ok' | 'pending' | 'exceeded' = 'ok'
      if (remaining > 0) status = 'pending'
      else if (remaining < 0) status = 'exceeded'
      return {
        ...grade,
        distributed,
        remaining,
        status,
      }
    })
  }, [grades, allocationMatrix])

  const overallTotals = useMemo(() => {
    const planned = grades.reduce((acc, curr) => acc + curr.plannedQuantity, 0)
    const distributed = Object.values(stageTotals).reduce((acc, curr) => acc + curr, 0)
    const remaining = Math.max(0, planned - distributed)
    return {
      planned,
      distributed,
      remaining,
      isBalanced: planned === distributed,
    }
  }, [grades, stageTotals])

  const subtitleInfo = useMemo(() => {
    const client = service?.clientName || 'Cliente'
    const sizes = grades.map((g) => g.size)
    const gradeRange = sizes.length > 1 ? `${sizes[0]}–${sizes[sizes.length - 1]}` : sizes[0] || 'Grade'
    let deliveryStr = ''
    if (service?.deliveryDate) {
      try {
        const d = new Date(service.deliveryDate)
        if (!isNaN(d.getTime())) {
          const day = String(d.getDate()).padStart(2, '0')
          const month = String(d.getMonth() + 1).padStart(2, '0')
          deliveryStr = ` · entrega ${day}/${month}`
        }
      } catch (err) {
        void err
      }
    }
    return `${client} · grade ${gradeRange}${deliveryStr}`
  }, [service, grades])

  const handleSaveDistribution = async () => {
    if (!serviceId) return
    try {
      setSaving(true)
      const payloadItems: Array<{
        size: string
        quantity: number
        stageId?: number
        serviceStageId?: number
        stepId?: number
        orderItemGradeId?: number
      }> = []

      grades.forEach((grade) => {
        stages.forEach((stage) => {
          const qty = allocationMatrix[grade.size]?.[stage.id] || 0
          payloadItems.push({
            size: grade.size,
            quantity: qty,
            stageId: stage.stageId,
            serviceStageId: stage.serviceStageId,
            stepId: stage.stepId,
            orderItemGradeId: grade.orderItemGradeId,
          })
        })
      })

      await axios.post(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/service-stage-grade`, {
        serviceId,
        ticketId: ticketId || service?.ticketId,
        distributions: payloadItems,
        items: payloadItems,
      })

      setFeedback({
        isOpen: true,
        message: 'Distribuição da grade salva com sucesso.',
        statusOk: true,
      })
      if (onSuccess) onSuccess()
      onClose()
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message || 'Erro ao salvar distribuição de grade.',
        statusOk: false,
      })
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderLeft>
            <HeaderTitleSection>
              <ModalTitle>
                {service?.codeOfOP || `OP-${serviceId}`} · {service?.productName || 'Produto'}
              </ModalTitle>
              <HeaderSubtitle>{subtitleInfo}</HeaderSubtitle>
            </HeaderTitleSection>
          </HeaderLeft>

          <HeaderRight>
            <HeaderMetricsGroup>
              <HeaderMetricBadge $variant="neutral">
                <MetricLabel>Total</MetricLabel>
                <MetricValue $variant="neutral">{overallTotals.planned}</MetricValue>
              </HeaderMetricBadge>
              <HeaderMetricBadge $variant="primary">
                <MetricLabel>Distribuídas</MetricLabel>
                <MetricValue $variant="primary">{overallTotals.distributed}</MetricValue>
              </HeaderMetricBadge>
              <HeaderMetricBadge $variant="success">
                <MetricLabel>A distribuir</MetricLabel>
                <MetricValue $variant="success">{overallTotals.remaining}</MetricValue>
              </HeaderMetricBadge>
            </HeaderMetricsGroup>
            <CloseButton onClick={onClose} title="Fechar">
              <X size={20} />
            </CloseButton>
          </HeaderRight>
        </ModalHeader>

        <ModalBody>
          {loading ? (
            <div style={{ padding: '60px', display: 'flex', justifyContent: 'center' }}>
              <OrdenoLoading />
            </div>
          ) : (
            <>
              <TableContainer>
                <GradeTable>
                  <TableHead>
                    <tr>
                      <th style={{ width: '70px' }}>Grade</th>
                      <th style={{ width: '95px' }}>Qtd. Total</th>
                      {stages.map((st) => (
                        <th key={st.id}>{st.name}</th>
                      ))}
                      <th style={{ width: '90px' }}>Restante</th>
                    </tr>
                  </TableHead>
                  <TableBody>
                    {sizeSummaries.map((row) => (
                      <tr key={row.size}>
                        <td>
                          <CellTam>{row.size}</CellTam>
                        </td>
                        <td>
                          <CellGrade>{row.plannedQuantity}</CellGrade>
                        </td>
                        {stages.map((st) => {
                          const currentVal = allocationMatrix[row.size]?.[st.id] ?? 0
                          return (
                            <td key={st.id}>
                              <QuantityInput
                                type="number"
                                min={0}
                                value={currentVal}
                                onChange={(e) =>
                                  handleQuantityChange(row.size, st.id, parseInt(e.target.value, 10))
                                }
                              />
                            </td>
                          )
                        })}
                        <td>
                          {row.status === 'ok' ? (
                            <CellRemainingOk>✓ ok</CellRemainingOk>
                          ) : row.status === 'pending' ? (
                            <CellRemainingPending>+{row.remaining}</CellRemainingPending>
                          ) : (
                            <CellRemainingExceeded>{row.remaining}</CellRemainingExceeded>
                          )}
                        </td>
                      </tr>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <tr>
                      <td></td>
                      <td>{overallTotals.planned}</td>
                      {stages.map((st) => (
                        <td key={st.id}>{stageTotals[st.id] || 0}</td>
                      ))}
                      <td></td>
                    </tr>
                  </TableFooter>
                </GradeTable>
              </TableContainer>

              <StatusNotice $isComplete={overallTotals.isBalanced}>
                {overallTotals.isBalanced ? (
                  <>
                    <CheckCircle2 size={16} />
                    <span>Distribuição completa — todas as peças alocadas em etapas.</span>
                  </>
                ) : (
                  <>
                    <AlertCircle size={16} />
                    <span>Ajuste as quantidades para balancear com o planejado</span>
                  </>
                )}
              </StatusNotice>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <CancelButton type="button" onClick={onClose}>
            Cancelar
          </CancelButton>
          <SaveButton type="button" onClick={handleSaveDistribution} disabled={saving || loading}>
            <Save size={16} />
            <span>{saving ? 'Salvando...' : 'Salvar Distribuição'}</span>
          </SaveButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  )
}

export default ManageGradeModal
